import { useContext, useEffect, useMemo, useState } from "react";
import { Stack } from "@fluentui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import { Document24Regular } from "@fluentui/react-icons";

import "katex/dist/katex.min.css";
import styles from "./Answer.module.css";

import { downloadFileAuthenticated, getFileAsBlob } from "../../api";
import { FileReference } from "../../api/models";
import { ChatIdContext } from "../../Context/ChatIdContext";
import { AnswerIcon } from "./AnswerIcon";

const BACKEND_URI = import.meta.env.VITE_BACKEND_URI;

/** Extract fileId from a /file?fileId=xxx URL */
const parseFileUrl = (url: string): { fileId: string; filename?: string } | null => {
    // Matches both "/file?fileId=xxx" and full "http://.../file?fileId=xxx"
    try {
        const u = new URL(url, "http://placeholder");
        if (u.pathname === "/file" && u.searchParams.has("fileId")) {
            return {
                fileId: u.searchParams.get("fileId")!,
                filename: u.searchParams.get("filename") ?? undefined,
            };
        }
    } catch {
        // not a valid URL
    }
    return null;
};

const isFileUrl = (url?: string) => url?.startsWith("/file?") || url?.startsWith(`${BACKEND_URI}/file?`);

interface Props {
    answer?: string;
    createdAt?: string;
    functionCall?: { functionName?: string; functionArguments?: string; functionResult?: string; functionFallback?: string };
    files?: FileReference[];
    skipTransform?: boolean;
    /** The answer was cut short — by its output limit or a content filter — and is partial. */
    incomplete?: boolean;
}

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".webp", ".svg", ".bmp", ".avif"];

/**
 * A file is an image only when its mime type says so, or (when mime is absent — as it is for
 * container_file_citation references) when its filename has an image extension. Unknown types
 * are treated as downloads, so a .csv/.md is never fed to the image renderer.
 */
const isImageFile = (file: FileReference) => {
    if (file.mimeType) return file.mimeType.startsWith("image/");
    const name = file.filename?.toLowerCase() ?? "";
    return IMAGE_EXTENSIONS.some(ext => name.endsWith(ext));
};

/**
 * A user-facing label for a file. Auto-generated artifacts arrive named after their raw
 * container id (e.g. "cfile_abc123.png"); show a generic label for those instead of the id.
 */
const fileDisplayName = (file: FileReference) => {
    const name = file.filename;
    if (name && !name.toLowerCase().startsWith("cfile_")) return name;
    return isImageFile(file) ? "Generated image" : "Generated file";
};

/** Inline image that loads via authenticated fetch for /file? URLs */
const AuthenticatedImage = ({ src, alt }: { src?: string; alt?: string }) => {
    const chatId = useContext(ChatIdContext);
    const [blobUrl, setBlobUrl] = useState<string | undefined>();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Returned from every path, not just the fetch: a URL an earlier run created has to be
        // released even when this run bails out early — chatId going undefined on New Chat would
        // otherwise leak one object URL per image on screen.
        const revokeBlobUrl = () =>
            setBlobUrl(prev => {
                if (prev && prev.startsWith("blob:")) URL.revokeObjectURL(prev);
                return undefined;
            });

        if (!src) return revokeBlobUrl;

        const parsed = parseFileUrl(src);
        if (!parsed) {
            // External URL — use directly
            setBlobUrl(src);
            return revokeBlobUrl;
        }

        // Without a conversation to fetch it against, the file is not addressable. The previous
        // run's cleanup has already cleared whatever was shown.
        if (!chatId) return revokeBlobUrl;

        // A fetch that finishes after the next run has started belongs to an image no longer on
        // screen: showing it would be wrong, and its object URL would never be revoked.
        const controller = new AbortController();
        setLoading(true);
        getFileAsBlob(chatId, parsed.fileId, parsed.filename, controller.signal)
            .then(url => {
                if (controller.signal.aborted) {
                    URL.revokeObjectURL(url);
                    return;
                }
                setBlobUrl(url);
            })
            .catch(() => {
                if (!controller.signal.aborted) setBlobUrl(undefined);
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => {
            controller.abort();
            setLoading(false);
            revokeBlobUrl();
        };
    }, [src, chatId]);

    if (loading) return <span className={styles.imageLoading}>Loading image...</span>;
    if (!blobUrl) return <span>{alt ?? "Image unavailable"}</span>;
    return <img src={blobUrl} alt={alt} style={{ maxWidth: "100%", borderRadius: 4 }} />;
};

/**
 * A file download that goes through an authenticated fetch. Downloads are scoped to the
 * conversation on screen; without one the file is not addressable, so the label is rendered as
 * plain text instead of a link that could not work.
 */
const FileDownloadLink = ({ fileId, filename, children }: { fileId: string; filename?: string; children: React.ReactNode }) => {
    const chatId = useContext(ChatIdContext);
    const [failed, setFailed] = useState(false);

    if (!chatId) {
        return <span>{children}</span>;
    }

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setFailed(false);
        downloadFileAuthenticated(chatId, fileId, filename).catch(() => setFailed(true));
    };

    return (
        <>
            <a href="#" onClick={handleClick} className={styles.downloadLink}>
                <Document24Regular />
                <span>{children}</span>
            </a>
            {failed && <span className={styles.downloadError}>Download failed</span>}
        </>
    );
};

/** Link renderer — file links get authenticated download, external links open normally */
const FileLinkRenderer = (props: any) => {
    const href = props.href as string | undefined;

    // F. sandbox: safety — leftover unresolved sandbox: links must never be clickable anchors.
    // Render the link text as plain inline text so the user can read the label but cannot click it.
    if (href?.toLowerCase().startsWith("sandbox:")) {
        return <span>{props.children}</span>;
    }

    if (isFileUrl(href)) {
        const parsed = parseFileUrl(href!);
        if (parsed) {
            return (
                <FileDownloadLink fileId={parsed.fileId} filename={parsed.filename}>
                    {props.children}
                </FileDownloadLink>
            );
        }
    }

    return (
        <a href={href} target="_blank" rel="noreferrer">
            {props.children}
        </a>
    );
};

/** Image renderer — /file? URLs use authenticated fetch, external URLs pass through */
const ImageRenderer = (props: any) => {
    const src = props.src as string | undefined;

    // F. sandbox: safety — an unresolved sandbox: src (e.g. mid-stream, before resolution) must
    // never reach <img>, or it renders as a broken-image icon. Show the alt text instead.
    if (src?.toLowerCase().startsWith("sandbox:")) {
        return <span>{props.alt ?? ""}</span>;
    }

    if (isFileUrl(src)) {
        return <AuthenticatedImage src={src} alt={props.alt} />;
    }

    // For external image URLs, render directly
    return <img src={src} alt={props.alt} style={{ maxWidth: "100%", borderRadius: 4 }} />;
};

/**
 * G. Math-transform hardening.
 *
 * Apply LaTeX/math substitutions ONLY to text that is outside fenced code blocks
 * (```...```) and inline code spans (`...`).  Running them over the whole string
 * would mangle `\[`, `\(`, `\text` etc. that appear legitimately in code samples,
 * regexes, and Windows paths.
 *
 * Strategy:
 *  1. Split the source into alternating non-code / code segments using a regex that
 *     matches fenced blocks (``` or ~~~, possibly with a language tag, multi-line)
 *     and inline backtick spans.
 *  2. Apply the three math replacements only to non-code segments.
 *  3. Rejoin and return.
 *
 * The split regex captures the full code token so we can round-trip it unchanged.
 */
const applyMathTransformsOutsideCode = (text: string): string => {
    // Matches, in order of priority:
    //   • Fenced blocks: opening fence (``` or ~~~) with optional language tag, then
    //     everything up to (and including) the matching closing fence on its own line.
    //   • Inline code: backtick-delimited span (non-greedy, no newlines).
    const codePattern = /(`{3,}|~{3,})[^\n]*\n[\s\S]*?\1[ \t]*(?:\n|$)|`[^`\n]*`/g;

    const segments: string[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = codePattern.exec(text)) !== null) {
        // Non-code segment before this code token
        segments.push(text.slice(lastIndex, match.index));
        // Code token — pushed verbatim (odd indices)
        segments.push(match[0]);
        lastIndex = match.index + match[0].length;
    }
    // Trailing non-code segment
    segments.push(text.slice(lastIndex));

    // Apply math transforms only to even-indexed segments (non-code)
    const transformed = segments.map((seg, i) => {
        if (i % 2 === 1) return seg; // code token — leave unchanged

        return seg
            .replace(/\\\[(.*?)\\\]/gs, "$$$1$$") // \[...\]  → $$...$$ (block math)
            .replace(/\\\((.*?)\\\)/gs, "$$\\textstyle $1$$") // \(...\)  → inline $$...$$
            .replace(/\\text{([^}]*)}/g, (_match, p1) => `\\text{${p1.replace(/&/g, "\\&")}}`);
    });

    return transformed.join("");
};

export const Answer = ({ answer, createdAt, functionCall, files, skipTransform, incomplete }: Props) => {
    const time = createdAt && new Date(createdAt).toLocaleTimeString();

    const markdown = useMemo(
        () => applyMathTransformsOutsideCode(answer ?? ""),
        [answer]
    );

    // E. Guaranteed file fallback.
    // A file is "referenced" (and so not repeated as a chip below the message) when a
    // well-formed /file?fileId=<id>[&filename=<name>] token appears in the raw answer text.
    // We match on BOTH the id and the filename: after upload the backend re-persists a file
    // under a new storage id, so the id in the inline link (the original container id) can
    // differ from files[].fileId (the persisted id) — matching the filename dedupes those.
    // We scan the raw `answer` (before math transforms) so transforms can't corrupt a URL.
    const referenced = useMemo(() => {
        const ids = new Set<string>();
        const names = new Set<string>();
        const regex = /\/file\?fileId=([^&"\s)]+)(?:&filename=([^&"\s)]+))?/g;
        let match;
        while ((match = regex.exec(answer ?? "")) !== null) {
            ids.add(decodeURIComponent(match[1]));
            if (match[2]) names.add(decodeURIComponent(match[2]).toLowerCase());
        }
        return { ids, names };
    }, [answer]);

    // Files not referenced in the text — show below the message
    const unreferencedFiles = useMemo(
        () =>
            files?.filter(
                f => !referenced.ids.has(f.fileId) && !(f.filename && referenced.names.has(f.filename.toLowerCase()))
            ) ?? [],
        [files, referenced]
    );
    const unreferencedImages = useMemo(() => unreferencedFiles.filter(isImageFile), [unreferencedFiles]);
    const unreferencedDownloads = useMemo(() => unreferencedFiles.filter(f => !isImageFile(f)), [unreferencedFiles]);

    return (
        <Stack className={styles.answerContainer} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                </Stack>
            </Stack.Item>

            <Stack.Item grow>
                <div className={styles.answerText}>
                    {skipTransform ? (
                        <ReactMarkdown children={markdown} />
                    ) : (
                        <ReactMarkdown
                            remarkPlugins={[remarkMath, remarkGfm]}
                            rehypePlugins={[rehypeKatex]}
                            components={{
                                a: FileLinkRenderer,
                                img: ImageRenderer
                            }}
                            children={markdown}
                        />
                    )}
                </div>

                {incomplete && (
                    <div className={styles.incompleteNote}>This answer was cut short before it finished. Ask a follow-up to continue.</div>
                )}

                {functionCall && (
                    <div className={styles.answerText}>
                        <div className={styles.functionName}>
                            Function called: <b>{functionCall.functionName}</b>
                        </div>
                        {functionCall?.functionFallback && (
                            <div className={styles.functionArguments}>
                                Fallback to: <b>{functionCall.functionFallback}</b>
                            </div>
                        )}
                        {functionCall.functionArguments && (
                            <p className={styles.functionArguments}>
                                <b>Arguments: </b>
                                <br />
                                {functionCall.functionArguments}
                            </p>
                        )}
                        {functionCall.functionResult && (() => {
                            // H. Render JSON results pretty-printed in a scrollable <pre>; fall back to raw text.
                            let resultNode: React.ReactNode;
                            try {
                                const parsed: unknown = JSON.parse(functionCall.functionResult);
                                if (parsed !== null && typeof parsed === "object") {
                                    resultNode = (
                                        <pre className={styles.functionResult}>
                                            {JSON.stringify(parsed, null, 2)}
                                        </pre>
                                    );
                                } else {
                                    resultNode = functionCall.functionResult;
                                }
                            } catch {
                                resultNode = functionCall.functionResult;
                            }
                            return (
                                <p className={styles.functionArguments}>
                                    <b>Results: </b> <br /> {resultNode}
                                </p>
                            );
                        })()}
                    </div>
                )}
            </Stack.Item>

            {/* Files the assistant generated but did not embed inline. Shown under a clear
                heading so they read as extras, not as the main content. Byte-identical
                duplicates of inline files are already removed server-side. */}
            {(unreferencedImages.length > 0 || unreferencedDownloads.length > 0) && (
                <Stack.Item>
                    <details className={styles.additionalFiles}>
                        <summary className={styles.additionalFilesHeading}>
                            Additional generated files ({unreferencedImages.length + unreferencedDownloads.length})
                        </summary>
                        <div className={styles.additionalFilesBody}>
                            {unreferencedImages.length > 0 && (
                                <Stack tokens={{ childrenGap: 8 }}>
                                    {unreferencedImages.map((f, i) => (
                                        <AuthenticatedImage
                                            key={i}
                                            src={`${BACKEND_URI}/file?fileId=${encodeURIComponent(f.fileId)}`}
                                            alt={fileDisplayName(f)}
                                        />
                                    ))}
                                </Stack>
                            )}
                            {unreferencedDownloads.length > 0 && (
                                <Stack horizontal wrap tokens={{ childrenGap: 8 }} style={{ marginTop: unreferencedImages.length > 0 ? 8 : 0 }}>
                                    {unreferencedDownloads.map((f, i) => (
                                        <FileDownloadLink key={i} fileId={f.fileId} filename={f.filename ?? undefined}>
                                            {fileDisplayName(f)}
                                        </FileDownloadLink>
                                    ))}
                                </Stack>
                            )}
                        </div>
                    </details>
                </Stack.Item>
            )}
            {time && <div className={styles.time}>{time}</div>}
        </Stack>
    );
};
